import { Box } from '@mui/material';
import { useNavigate } from 'react-router';

import Crown from '@/assets/icons/Crown';
import Button from '@/components/Buttons/Button';
import { DATAHUB_SPACING_LG } from '@/features/DataHub/constants/ui';
import { useExport } from '@/features/DataHub/modules/Export/context/ExportContext';
import type { ButtonConfig } from '@/features/DataHub/modules/Export/types/exportConfig.types';
import { useAppDispatch } from '@/hooks/redux-hooks';
import useUserDetails from '@/hooks/useUserDetails';
import { closeDrawer } from '@/slices/drawerSlice';

const DataFooterButtons = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isUserPremium, isUserExpired } = useUserDetails();
  const {
    exportOption,
    handleExportData,
    exportDataWithParams,
    handleCopyToClipBoard,
    exportCurlRequestString,
    exportApiRequestString,
    capturePosthogEvent,
    loading,
    previewLoading,
    previewDownloadFn,
    exportName,
    setExcelExportStatus
  } = useExport();

  const isIntegrationExport = exportOption?.preview?.type === 'integration';

  const shouldShowUpgradeButtons = !isUserPremium || isUserExpired;

  const buttons: ButtonConfig[] = shouldShowUpgradeButtons
    ? [{ label: 'Upgrade now', action: 'custom' }]
    : (exportOption?.buttons ?? []);

  const handleButtonClick = (button: ButtonConfig) => {
    if (button.action === 'copy') {
      handleCopyToClipBoard(exportCurlRequestString);
    } else if (button.action === 'download' || button.action === 'export') {
      capturePosthogEvent();
      const isExcel = exportOption?.id === 'excel';

      if (isIntegrationExport && exportOption?.id) {
        setExcelExportStatus('loading');
        exportDataWithParams({
          type: 'third-party',
          exportSubType: exportOption.id
        })
          .then(() => {
            setExcelExportStatus('queued');
          })
          .catch(() => {
            setExcelExportStatus('idle');
          });
        return;
      }

      // Show loading modal for non-Excel exports (Excel handles its own loading state)
      if (!isExcel) {
        setExcelExportStatus('loading');
      }

      // First save to past exports, then download
      handleExportData()
        .then(() => {
          if (previewDownloadFn) {
            previewDownloadFn();
          }
          if (!isExcel) {
            setExcelExportStatus('idle');
          }
        })
        .catch(() => {
          if (!isExcel) {
            setExcelExportStatus('idle');
          }
        });
    } else if (button.action === 'launch') {
      capturePosthogEvent();
      setExcelExportStatus('loading');
      if (exportApiRequestString) {
        window.open(exportApiRequestString, '_blank', 'noopener,noreferrer');
      }
      handleExportData()
        .then(() => {
          setExcelExportStatus('idle');
        })
        .catch(() => {
          setExcelExportStatus('idle');
        });
    } else if (button.label === 'Upgrade now') {
      dispatch(closeDrawer());
      navigate('/manage-subscription');
    } else if (button.onClick) {
      capturePosthogEvent();
      button.onClick();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '96px',
        width: '100%',
        backgroundColor: 'background.main',
        borderTop: '1px solid',
        borderColor: 'info.dark',
        p: DATAHUB_SPACING_LG,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: DATAHUB_SPACING_LG,
        '& > *': {
          flex: 1
        }
      }}
    >
      {buttons.length === 1 && (
        <Button
          sx={{
            color: 'primary.main',
            borderColor: 'primary.main'
          }}
          variant='outlined'
          onClick={() => {
            dispatch(closeDrawer());
          }}
        >
          Cancel
        </Button>
      )}
      {buttons.map(button => {
        const isExportAction =
          button.action === 'download' || button.action === 'export' || button.action === 'launch';
        const isNameInvalid = !exportName || exportName.trim() === '';
        const isDisabled =
          button.disabled || (isExportAction && (loading || previewLoading || isNameInvalid));

        return (
          <Button
            key={button.label}
            disabled={isDisabled}
            startIcon={button.label === 'Upgrade now' ? <Crown /> : undefined}
            variant={button.variant ?? 'contained'}
            onClick={() => {
              handleButtonClick(button);
            }}
          >
            {button.label}
          </Button>
        );
      })}
    </Box>
  );
};

export default DataFooterButtons;
